const inputClass =
  "w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-black placeholder-gray-400 focus:outline-none focus:border-black focus:bg-white transition-all duration-200";

const labelClass =
  "block text-xs font-semibold text-gray-500 uppercase tracking-widest mb-1.5";

const FieldGroup = ({ label, children }) => (
  <div>
    <label className={labelClass}>{label}</label>
    {children}
  </div>
);

const DetailsSection = ({
  details,
  onChange,
  editOtherLang,
  setEditOtherLang,
  languages = [],
  selectedLang,
  setSelectedLang,
  translations = {},
  setTranslations,
  logoPreview,
  defaultPreview,
  onImageChange
}) => {
  const handlePhoneLikeChange = (field, value) => {
    // allow leading + and digits only
    const cleaned = value.replace(/[^\d+]/g, "");
    const normalized = cleaned.startsWith("+")
      ? "+" + cleaned.slice(1).replace(/[^\d]/g, "")
      : cleaned.replace(/[^\d]/g, "");
    onChange(field, normalized);
  };

  const handleDecimalChange = (field, value) => {
    // digits with optional single dot (e.g., 0.05)
    const cleaned = value.replace(/[^\d.]/g, "");
    const parts = cleaned.split(".");
    const normalized =
      parts.length <= 1 ? cleaned : `${parts[0]}.${parts.slice(1).join("")}`;
    onChange(field, normalized);
  };

  const handleLangFieldChange = (langCode, field, value) => {
    setTranslations((prev) => ({
      ...prev,
      [langCode]: {
        ...(prev[langCode] || {}),
        [field]: value,
      },
    }));
  };

  // Only show non-English languages in the multi-language toggle
  const otherLanguages = (languages || []).filter(
    (lang) => lang.code && lang.code.toLowerCase() !== "en",
  );

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
      <div className="flex items-center gap-2 pb-4 border-b border-gray-100 mb-4">
        <div className="w-7 h-7 rounded-lg bg-black flex items-center justify-center text-white flex-shrink-0">
          <span className="text-xs font-semibold">1</span>
        </div>
        <div>
          <h2 className="text-sm font-semibold text-gray-900">Details</h2>
          <p className="text-xs text-gray-500">
            Basic details and billing information.
          </p>
        </div>
      </div>

      {/* Multi-language toggle (for name + address) */}
      {otherLanguages.length > 0 && (
        <div className="mb-4 flex flex-col gap-3">
          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="checkbox"
              className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500 cursor-pointer"
              checked={editOtherLang}
              onChange={(e) => setEditOtherLang?.(e.target.checked)}
            />
            <span className="text-xs font-semibold text-gray-700">
              Edit multiple languages (Name &amp; Address)
            </span>
          </label>

          {editOtherLang && (
            <div className="space-y-3">
              <div className="flex flex-wrap gap-2">
                {otherLanguages.map((lang) => (
                  <button
                    key={lang.code}
                    type="button"
                    onClick={() => setSelectedLang?.(lang.code)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${selectedLang === lang.code
                        ? "bg-black text-white border-black shadow-sm"
                        : "bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100"
                      }`}
                  >
                    {lang.name}
                  </button>
                ))}
              </div>

              {selectedLang &&
                selectedLang.toLowerCase() !== "en" && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                    <FieldGroup label={`Name (${selectedLang.toUpperCase()})`}>
                      <input
                        type="text"
                        value={translations[selectedLang]?.name || ""}
                        onChange={(e) =>
                          handleLangFieldChange(
                            selectedLang,
                            "name",
                            e.target.value,
                          )
                        }
                        className={inputClass}
                        dir={
                          languages.find((l) => l.code === selectedLang)
                            ?.direction || "ltr"
                        }
                        placeholder={`Enter name in ${selectedLang}`}
                      />
                    </FieldGroup>
                    <FieldGroup label={`Address (${selectedLang.toUpperCase()})`}>
                      <input
                        type="text"
                        value={translations[selectedLang]?.address || ""}
                        onChange={(e) =>
                          handleLangFieldChange(
                            selectedLang,
                            "address",
                            e.target.value,
                          )
                        }
                        className={inputClass}
                        dir={
                          languages.find((l) => l.code === selectedLang)
                            ?.direction || "ltr"
                        }
                        placeholder={`Enter address in ${selectedLang}`}
                      />
                    </FieldGroup>
                  </div>
                )}
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FieldGroup label="Name">
          <input
            type="text"
            value={details.name}
            onChange={(e) => onChange("name", e.target.value)}
            placeholder="Enter name"
            className={inputClass}
          />
        </FieldGroup>

        <FieldGroup label="Address">
          <input
            type="text"
            value={details.address}
            onChange={(e) => onChange("address", e.target.value)}
            placeholder="Enter full address"
            className={inputClass}
          />
        </FieldGroup>

        <FieldGroup label="Contact Number">
          <input
            type="tel"
            inputMode="tel"
            value={details.contact}
            onChange={(e) => handlePhoneLikeChange("contact", e.target.value)}
            placeholder="Enter contact number (e.g. +9198...)"
            className={inputClass}
          />
        </FieldGroup>

        <FieldGroup label="WhatsApp Number">
          <input
            type="tel"
            inputMode="tel"
            value={details.whatsapp}
            onChange={(e) => handlePhoneLikeChange("whatsapp", e.target.value)}
            placeholder="Enter WhatsApp number (e.g. +9191...)"
            className={inputClass}
          />
        </FieldGroup>

        <FieldGroup label="GST Number">
          <input
            type="text"
            value={details.gst}
            onChange={(e) => onChange("gst", e.target.value)}
            placeholder="Enter GST number"
            className={inputClass}
          />
        </FieldGroup>

        <FieldGroup label="License Number (Optional)">
          <input
            type="text"
            value={details.licenseNumber}
            onChange={(e) => onChange("licenseNumber", e.target.value)}
            placeholder="Enter license number"
            className={inputClass}
          />
        </FieldGroup>

        <FieldGroup label="Tax Type (gst,vat)">
          <input
            type="text"
            value={details.taxType}
            onChange={(e) => onChange("taxType", e.target.value)}
            placeholder="Enter tax type "
            className={inputClass}
          />
        </FieldGroup>

        <FieldGroup label="Tax Rate ">
          <input
            type="text"
            inputMode="decimal"
            value={details.gstRate}
            onChange={(e) => handleDecimalChange("gstRate", e.target.value)}
            placeholder="Enter tax rate"
            className={inputClass}
          />
        </FieldGroup>

        <FieldGroup label="Opening Time">
          <input
            type="time"
            value={details.openingTime}
            onChange={(e) => onChange("openingTime", e.target.value)}
            className={inputClass}
          />
        </FieldGroup>

        <FieldGroup label="Closing Time">
          <input
            type="time"
            value={details.closingTime}
            onChange={(e) => onChange("closingTime", e.target.value)}
            className={inputClass}
          />
        </FieldGroup>
        <FieldGroup label="Restaurant Logo">

          <input
            type="file"
            accept=".jpg,.jpeg,.png"
            onChange={(e) => onImageChange(e, "logo")}
            className={inputClass}
          />

          {logoPreview && (
            <img
              src={logoPreview}
              className="mt-2 h-20 object-contain"
            />
          )}

        </FieldGroup>
        <FieldGroup label="Default Product Image">

          <input
            type="file"
            accept=".jpg,.jpeg,.png"
            onChange={(e) => onImageChange(e, "default")}
            className={inputClass}
          />

          {defaultPreview && (
            <img
              src={defaultPreview}
              className="mt-2 h-20 object-contain"
            />
          )}

        </FieldGroup>
      </div>
    </div>
  );
};

export default DetailsSection;

